import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, computed, effect, inject, signal, untracked, viewChild, viewChildren } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, Subject, catchError, filter, map, of, switchMap, tap, timeout, timer } from 'rxjs';
import { SearchOverlayService } from '../../services/search-overlay.service';
import { OsDetectorService } from '../../services/os-detector.service';
import { GlobalSearchFilterService } from '../../services/global-search-filter.service';
import { AccountService } from 'app/core/auth/account.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { ArtemisTranslatePipe } from 'app/foundation/pipes/artemis-translate.pipe';
import { DialogModule } from 'primeng/dialog';
import { GlobalSearchNavigationViewComponent } from 'app/core/navbar/global-search/components/views/navigation-view/global-search-navigation-view.component';
import { MIN_SEARCH_QUERY_LENGTH, SEARCH_DEBOUNCE_MS, SearchResultView } from 'app/core/navbar/global-search/components/views/search-result-view.directive';
import { GlobalSearchResult } from 'app/openapi/model/global-search-result';
import { GlobalSearchApi } from 'app/openapi/api/global-search-api';
import { SearchInputComponent } from './search-input/search-input.component';
import { GlobalSearchFilterMenuComponent } from './filter-menu/global-search-filter-menu.component';
import { SEARCH_RESULTS_LISTBOX_ID, filterOptionDomId, searchResultOptionDomId } from '../../models/search-menu.model';
import { FilterToken } from '../../models/search-token.model';
import { removeTokenAt } from '../../models/search-token.util';
import { IrisSearchAvailabilityService } from '../../services/iris-search-availability.service';
import { LectureSearchService } from '../../services/lecture-search.service';
import { LECTURE_CONTENT_TYPE, mapLectureContentResult } from '../../models/lecture-content-result.util';
import { TranslateService } from '@ngx-translate/core';
import { CourseStorageService } from 'app/course/manage/services/course-storage.service';
import { CourseManagementService } from 'app/course/manage/services/course-management.service';
import { CourseOverviewGuard } from 'app/course/overview/course-overview/course-overview-guard';
import { CourseAvailableTabs } from 'app/course/shared/entities/course-available-tabs.model';
import { COURSE_PAGE_TYPE, matchCoursePages } from '../../models/course-page-results.util';

/** Iris content search is a vector search behind a network hop, so it gets its own, longer ceiling. */
export const CONTENT_SEARCH_TIMEOUT_MS = 5_000;

/** Number of content hits requested per search. */
const CONTENT_SEARCH_LIMIT = 10;

/** Results per page of the metadata search, and the most the server returns for one search. */
export const DEFAULT_RESULT_LIMIT = 10;
export const EXPANDED_RESULT_LIMIT = 25;

interface SearchState {
    query: string;
    /** How many results to ask for; the default page size when absent. */
    limit?: number;
}

/**
 * Keys that move between or remove keyboard-highlighted chips, and that the text needs once the user moved on. Enter
 * is left out: it has its own guards, and re-picking the chip that is still visibly highlighted is what it shows.
 */
const CHIP_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete']);

@Component({
    selector: 'jhi-global-search-modal',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogModule, FaIconComponent, ArtemisTranslatePipe, GlobalSearchNavigationViewComponent, SearchInputComponent, GlobalSearchFilterMenuComponent],
    providers: [GlobalSearchFilterService],
    templateUrl: './global-search-modal.component.html',
    styleUrls: ['./global-search-modal.component.scss'],
})
export class GlobalSearchModalComponent implements OnDestroy {
    protected readonly overlay = inject(SearchOverlayService);
    private readonly osDetector = inject(OsDetectorService);
    private readonly document = inject(DOCUMENT);
    private readonly accountService = inject(AccountService);
    private readonly router = inject(Router);
    private readonly searchService = inject(GlobalSearchApi);
    // Owns the filter composition (tokens, value menu, guided picker, chip edit). The component keeps the search
    // pipeline, keyboard navigation, results, view, and overlay; it wires the two side-effects the store needs.
    protected readonly filter = inject(GlobalSearchFilterService);
    private readonly availability = inject(IrisSearchAvailabilityService);
    private readonly lectureSearchService = inject(LectureSearchService);
    private readonly courseStorageService = inject(CourseStorageService);
    private readonly translateService = inject(TranslateService);
    private readonly courseManagementService = inject(CourseManagementService);
    private readonly courseOverviewGuard = inject(CourseOverviewGuard);

    protected readonly faArrowUp = faArrowUp;
    protected readonly faArrowDown = faArrowDown;
    protected readonly searchInputComponent = viewChild<SearchInputComponent>(SearchInputComponent);

    // Filter composition state + derived views, owned by GlobalSearchFilterService and re-exposed by reference so the
    // template and existing tests address them on the component unchanged while the logic lives in the store.
    protected readonly searchQuery = this.filter.searchQuery;
    protected readonly tokens = this.filter.tokens;
    protected readonly typesParam = this.filter.typesParam;
    protected readonly excludeTypesParam = this.filter.excludeTypesParam;
    protected readonly courseIdsParam = this.filter.courseIdsParam;
    protected readonly excludeCourseIdsParam = this.filter.excludeCourseIdsParam;
    protected readonly activeFilters = this.filter.activeFilters;
    protected readonly selectedChip = this.filter.selectedChip;
    /** Focus and cursor when a chip got highlighted; Backspace/Delete removes the chip only while both stay put. */
    private chipKeyOrigin?: { target: Element | null; selectionStart: number | null; selectionEnd: number | null };
    protected readonly chips = this.filter.chips;
    protected readonly operator = this.filter.operator;
    protected readonly menuActiveIndex = this.filter.menuActiveIndex;
    protected readonly menuOptions = this.filter.menuOptions;
    protected readonly operatorValueValid = this.filter.operatorValueValid;
    protected readonly filterPickerOpen = this.filter.filterPickerOpen;
    protected readonly editingChip = this.filter.editingChip;
    protected readonly filterMenuOpen = this.filter.filterMenuOpen;
    protected readonly searchText = this.filter.searchText;
    protected readonly deadEnd = this.filter.deadEnd;
    protected readonly deadEndMessage = this.filter.deadEndMessage;
    protected readonly emptyMenuReasonKey = this.filter.emptyMenuReasonKey;
    protected readonly canGoBack = this.filter.canGoBack;
    protected readonly contentSearchActive = this.filter.contentSearchActive;

    // OS-aware label for the filter-picker shortcut shown on the Filter button (⌘F on Mac, Ctrl+F elsewhere).
    protected readonly filterShortcutLabel = computed<string>(() => (this.osDetector.isMac() ? '⌘F' : 'Ctrl+F'));
    // DOM id of the highlighted filter-menu option, for the search input's aria-activedescendant (the menu now
    // renders in the results pane, so the combobox references it across components by id).
    /** How many selectable rows the results listbox renders; it mirrors the navigation view's template. */
    private readonly renderedResultRows = computed(() => {
        const contentRow = !this.contentSearchActive() && this.contentPreview().length > 0 ? 1 : 0;
        const resultRows = this.showResults() && !this.isLoading() && !this.searchError() ? this.results().length : 0;
        return contentRow + resultRows;
    });
    /** The results listbox while it shows rows; the filter menu takes over the input's popup while it is open. */
    protected readonly resultsListboxId = computed<string | undefined>(() => (!this.filterMenuOpen() && this.renderedResultRows() > 0 ? SEARCH_RESULTS_LISTBOX_ID : undefined));
    /** The selected result row, so a screen reader announces it while the focus stays in the input; none while it is not rendered. */
    protected readonly activeResultId = computed<string | undefined>(() => {
        const index = this.selectedIndex();
        return this.resultsListboxId() && index >= 0 && index < this.renderedResultRows() ? searchResultOptionDomId(index) : undefined;
    });
    protected readonly activeOptionId = computed<string | undefined>(() => {
        const option = this.menuOptions()[this.menuActiveIndex()];
        return option ? filterOptionDomId(option.id) : undefined;
    });
    protected readonly results = signal<GlobalSearchResult[]>([]);
    protected readonly isLoading = signal<boolean>(false);
    /** Slide and video hits for the current query, shown as one row above the results while content search is not active. */
    protected readonly contentPreview = signal<GlobalSearchResult[]>([]);
    private readonly contentPreviewSubject = new Subject<SearchState | null>();
    /** The result limit of the search that produced the current results. */
    private readonly resultLimit = signal(DEFAULT_RESULT_LIMIT);
    protected readonly canShowMore = computed(() => !this.contentSearchActive() && this.resultLimit() < EXPANDED_RESULT_LIMIT && this.results().length >= this.resultLimit());
    protected readonly hasSearched = signal<boolean>(false);
    protected readonly searchError = signal<string | undefined>(undefined);
    protected readonly selectedIndex = signal(-1);
    private readonly allViews = viewChildren(SearchResultView);
    private readonly maxIndex = computed(() => (this.allViews()[0]?.itemCount() ?? 0) - 1);
    private readonly searchSubject = new Subject<SearchState | null>();
    /** Last text pushed into the pipeline, so composing an operator does not re-request the unchanged query. */
    private lastSearchText = '';
    // Cache for placeholder results (empty-query + filter) so re-adding a filter serves from cache
    private readonly placeholderCache = new Map<string, GlobalSearchResult[]>();

    // Computed properties
    protected hasResults = computed(() => this.results().length > 0);
    protected showResults = computed(() => this.isLoading() || this.hasSearched());
    /**
     * Whether a pane other than the filter menu can currently render: a result set or its loading skeleton.
     * On a fresh palette there is nothing behind the menu, so leaving it would land on a blank pane.
     */
    protected readonly hasPaneBehindFilterMenu = computed(() => this.showResults());
    /**
     * Where the filter menu's back control leads, or undefined when there is nowhere to go. One level deep it
     * returns to the filter root; at the root it returns to the results behind the menu; on a fresh palette
     * there is nothing behind it, so no back control is offered at all rather than one that does nothing.
     */
    protected readonly backDestination = computed<'results' | 'filters' | 'exclude' | undefined>(() => {
        if (this.deadEnd()) {
            // A dead end has no level behind it; it offers the literal search instead.
            return undefined;
        }
        if (this.canGoBack()) {
            // A negated value list was opened from the exclude chooser, not from the root, so it returns there.
            return this.operator()?.negate ? 'exclude' : 'filters';
        }
        return this.filterMenuOpen() && this.hasPaneBehindFilterMenu() ? 'results' : undefined;
    });
    /**
     * i18n key for the menu header. Whenever a back control is offered the header is that control and names its
     * destination, so the chevron means one thing at every depth. Only the root of a fresh palette, which has no
     * way back, falls through to naming the level instead.
     */
    protected readonly menuHeaderKey = computed(() => {
        switch (this.backDestination()) {
            case 'results':
                return 'global.search.backToResults';
            case 'filters':
                return 'global.search.backToFilters';
            case 'exclude':
                return 'global.search.backToExcludeOptions';
            default:
                return 'global.search.addFilter';
        }
    });
    /**
     * Whether the Filter button has nothing left to do. It is a one-way control ("take me to filters"), so it is
     * dead exactly when the menu is already showing at its root, whatever sits behind that. One level deep, in a
     * value list or the exclude chooser, it still returns to the root, so it stays live there.
     */
    protected readonly filterTriggerDisabled = computed(() => this.filterMenuOpen() && !this.operator() && !this.filter.excludeMode());
    /**
     * i18n key for the footer's Escape hint, worded to match the back control it mirrors. Escape steps back a level
     * inside the filter menu, and also when leaving the menu reveals the pane behind it; only at the home screen, with
     * nothing behind, does it close.
     */
    protected readonly escapeHintKey = computed(() => {
        if (this.deadEnd()) {
            // Escape cannot mean "cancel" here: the operator is not a filter, so backing out of it would
            // delete text the user typed as a search term.
            return 'global.search.searchAnyway';
        }
        switch (this.backDestination()) {
            case 'results':
                return 'global.search.toResults';
            case 'filters':
                return 'global.search.toFilters';
            case 'exclude':
                return 'global.search.toExcludeOptions';
            default:
                return 'global.search.toClose';
        }
    });

    ngOnDestroy(): void {
        if (this.overlay.isOpen()) {
            this.overlay.close();
        }
    }

    /** The element that had the focus when the palette opened, and the page it was on. */
    private focusOrigin?: { element: HTMLElement; url: string };

    /** The available tabs of the one course the search is scoped to, which decide the guarded course pages it may offer. */
    private readonly scopedCourseTabs = signal<{ courseId: number; tabs: CourseAvailableTabs } | undefined>(undefined);

    constructor() {
        // Read directly from the endpoint rather than through CourseAvailableTabsService, whose held tabs belong to the
        // course page the user is on and must not be replaced by a course picked in the palette.
        effect((onCleanup) => {
            const courseIds = this.courseIdsParam();
            if (!this.overlay.isOpen() || courseIds.length !== 1) {
                return;
            }
            const courseId = courseIds[0];
            if (untracked(this.scopedCourseTabs)?.courseId === courseId) {
                return;
            }
            const subscription = this.courseManagementService
                .getCourseAvailableTabs(courseId)
                .pipe(catchError(() => EMPTY))
                .subscribe((tabs) => {
                    this.scopedCourseTabs.set({ courseId, tabs });
                    // The tabs can arrive after the search results: offer the pages they allow next to the hits already shown.
                    if (this.hasSearched() && !this.contentSearchActive()) {
                        this.replaceResultsKeepingSelection([...this.coursePageResults(), ...this.results().filter((result) => result.type !== COURSE_PAGE_TYPE)]);
                    }
                });
            onCleanup(() => subscription.unsubscribe());
        });

        // Wire the store's side-effects: a token change re-runs the search pipeline; menu/picker actions refocus input.
        this.filter.configure({
            applyTokens: (tokens) => this.applyTokens(tokens),
            requestFocus: () => this.focusInput(),
            exitFilterMenu: () => this.exitFilterMenu(),
            refreshSearch: () => this.applyTokens(this.tokens()),
        });

        // Reset selection whenever the query changes; reading searchQuery() registers it as a reactive dependency.
        effect(() => {
            this.searchQuery();
            this.selectedIndex.set(-1);
            this.selectedChip.set(-1);
            this.menuActiveIndex.set(0);
        });

        // Search pipeline: switchMap + timer acts as a cancellable debounce.
        // Emitting null (reset) cancels any pending debounce timer via switchMap unsubscription.
        this.searchSubject
            .pipe(
                switchMap((event) => {
                    // Null (reset) — execute synchronously, cancel any pending debounce
                    if (event === null) {
                        this.results.set([]);
                        this.hasSearched.set(false);
                        this.isLoading.set(false);
                        this.searchError.set(undefined);
                        return EMPTY;
                    }

                    const query = event.query;
                    const types = this.typesParam();
                    const excludeTypes = this.excludeTypesParam();
                    const courseIds = this.courseIdsParam();
                    const excludeCourseIds = this.excludeCourseIdsParam();
                    const hasFilter = types !== undefined || excludeTypes !== undefined || courseIds.length > 0 || excludeCourseIds.length > 0;
                    const trimmedQuery = query?.trim() || '';
                    const hasValidQuery = trimmedQuery.length >= MIN_SEARCH_QUERY_LENGTH;
                    const isTooShort = trimmedQuery.length > 0 && !hasValidQuery;

                    // No input at all and no filter — clear results synchronously. The content filter carries no
                    // server type, so it is named here explicitly to stay a filter.
                    if (!trimmedQuery.length && !hasFilter && !this.contentSearchActive()) {
                        this.results.set([]);
                        this.hasSearched.set(false);
                        this.isLoading.set(false);
                        this.searchError.set(undefined);
                        return EMPTY;
                    }

                    // Query too short for server search (1-2 chars) — show loading skeleton
                    // while user is typing, then after debounce show the "too short" message
                    // without sending a request to the server (even when a filter is active).
                    if (isTooShort && !this.contentSearchActive()) {
                        this.isLoading.set(true);
                        this.searchError.set(undefined);
                        return timer(SEARCH_DEBOUNCE_MS).pipe(
                            switchMap(() => {
                                this.isLoading.set(false);
                                this.hasSearched.set(true);
                                return of([]);
                            }),
                        );
                    }

                    // Slides and videos live in the Iris collections, not in the searchable entities, so that
                    // filter never reaches the metadata search.
                    if (this.contentSearchActive()) {
                        return this.searchLectureContent(trimmedQuery, hasValidQuery);
                    }

                    this.searchError.set(undefined);
                    const searchQuery = hasValidQuery ? trimmedQuery : '';
                    const courseIdsParam = courseIds.length ? courseIds : undefined;
                    const excludeCourseIdsParam = excludeCourseIds.length ? excludeCourseIds : undefined;
                    const limit = event.limit ?? DEFAULT_RESULT_LIMIT;
                    this.resultLimit.set(limit);
                    const cacheKey = this.filterCacheKey(types, excludeTypes, courseIds, excludeCourseIds, limit);

                    // Empty query with filter — serve from cache synchronously if available
                    if (!hasValidQuery && hasFilter) {
                        const cached = this.placeholderCache.get(cacheKey);
                        if (cached) {
                            this.isLoading.set(false);
                            return of(cached);
                        }
                    }

                    // Network search — debounce, then fire HTTP request
                    this.isLoading.set(true);
                    return timer(SEARCH_DEBOUNCE_MS).pipe(
                        switchMap(() =>
                            this.searchService.globalSearch(searchQuery, types, excludeTypes, courseIdsParam, excludeCourseIdsParam, limit).pipe(
                                tap((results) => {
                                    if (!hasValidQuery && hasFilter) {
                                        this.placeholderCache.set(cacheKey, results);
                                    }
                                }),
                                catchError(() => {
                                    this.isLoading.set(false);
                                    this.searchError.set('global.search.searchFailed');
                                    return of([]);
                                }),
                            ),
                        ),
                    );
                }),
                takeUntilDestroyed(),
            )
            .subscribe((results) => {
                this.results.set([...this.coursePageResults(), ...results]);
                this.selectedIndex.set(-1);
                this.isLoading.set(false);
                this.hasSearched.set(true);
            });

        // A light, parallel content search: it only feeds the row that offers to switch to the slides-and-videos
        // search, so a failure just hides the row.
        this.contentPreviewSubject
            .pipe(
                switchMap((event) => {
                    if (event === null) {
                        return of([]);
                    }
                    const courseIds = this.courseIdsParam();
                    const excludeCourseIds = this.excludeCourseIdsParam();
                    return timer(SEARCH_DEBOUNCE_MS).pipe(
                        switchMap(() =>
                            this.lectureSearchService
                                .search(event.query, CONTENT_SEARCH_LIMIT, courseIds.length ? courseIds : undefined, excludeCourseIds.length ? excludeCourseIds : undefined, true)
                                .pipe(
                                    timeout(CONTENT_SEARCH_TIMEOUT_MS),
                                    map((results) => results.map(mapLectureContentResult)),
                                    catchError(() => of([])),
                                ),
                        ),
                    );
                }),
                takeUntilDestroyed(),
            )
            .subscribe((hits) => this.setContentPreview(hits));

        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                takeUntilDestroyed(),
            )
            .subscribe(() => {
                if (this.overlay.isOpen()) {
                    this.overlay.close();
                }
            });

        // Reset state when modal is closed; apply context filters when opened.
        // untracked() prevents signals read inside applyContextFilters/resetSearch
        // (e.g. tokens) from becoming reactive dependencies of this effect —
        // only overlay.isOpen() should trigger it.
        effect(() => {
            const isOpen = this.overlay.isOpen();
            untracked(() => {
                if (isOpen) {
                    // The dialog is not rendered yet, so this is still the element the user opened the palette from.
                    const active = this.document.activeElement;
                    this.focusOrigin = active instanceof HTMLElement ? { element: active, url: this.router.url } : undefined;
                    this.applyContextFilters();
                    // Home screen: open the guided filter picker so filters are the first thing offered. When a course
                    // context was applied (tokens present), show its scoped results instead of the picker.
                    if (this.tokens().length === 0) {
                        this.filter.openFilterPicker();
                    }
                } else {
                    this.resetSearch();
                }
            });
        });
    }

    /**
     * Parses the current URL to detect course context and tab, then pre-populates the course and type
     * tokens accordingly and triggers a search so results are displayed immediately.
     * Supports both student view (/courses/:id) and instructor view (/course-management/:id).
     */
    private applyContextFilters(): void {
        const newTokens = this.filter.deriveContextTokens(this.router.url);
        if (!newTokens) {
            return;
        }
        this.tokens.set(newTokens);
        this.lastSearchText = '';
        this.searchSubject.next({ query: '' });
    }

    /**
     * The pages of the one course the search is scoped to whose name matches the typed text, such as "grades" for the
     * statistics page. Offered only for a plain search in a single course, where the course is unambiguous.
     */
    /**
     * Replaces the shown results while keeping the selected one selected: the views select by index, and the slides-and-
     * videos row, when shown, takes the first index before the results.
     */
    private replaceResultsKeepingSelection(results: GlobalSearchResult[]): void {
        const rowOffset = this.contentPreview().length > 0 && !this.contentSearchActive() ? 1 : 0;
        const selected = this.selectedIndex();
        // IDs are only unique per entity type: an exercise and a lecture can both have ID 7.
        const selectedResult = selected >= rowOffset ? this.results()[selected - rowOffset] : undefined;
        this.results.set(results);
        if (selectedResult !== undefined) {
            const index = results.findIndex((result) => result.type === selectedResult.type && result.id === selectedResult.id);
            this.selectedIndex.set(index >= 0 ? index + rowOffset : -1);
        }
    }

    private coursePageResults(): GlobalSearchResult[] {
        const courseIds = this.courseIdsParam();
        if (this.contentSearchActive() || courseIds.length !== 1 || this.typesParam() !== undefined || this.excludeTypesParam() !== undefined) {
            return [];
        }
        const courseId = courseIds[0];
        const scopedTabs = this.scopedCourseTabs();
        const tabs = scopedTabs?.courseId === courseId ? scopedTabs.tabs : undefined;
        // The guard's own rule decides, so the palette offers exactly the tabs the course page would open.
        const isAvailable = (segment: string) => !!tabs && this.courseOverviewGuard.decideAccess(courseId, tabs, segment) === true;
        return matchCoursePages(this.searchText(), courseId, this.courseStorageService.getCourse(courseId)?.title, (key) => this.translateService.instant(key), isAvailable);
    }

    /**
     * Asks for the slide and video hits of the query when the content row can be offered: the user opted into AI, the
     * slides-and-videos search is not already active, and the query is long enough to search.
     */
    private requestContentPreview(query: string): void {
        const offersRow = !this.contentSearchActive() && this.availability.contentSearchAvailable() && query.trim().length >= MIN_SEARCH_QUERY_LENGTH;
        // The hits of the previous query or scope no longer apply; the row stays hidden until the new ones arrive.
        this.setContentPreview([]);
        this.contentPreviewSubject.next(offersRow ? { query: query.trim() } : null);
    }

    /**
     * Shows new preview hits. The row is the first selectable item, so a selected result keeps its selection when the
     * row appears or disappears instead of the selection pointing at whatever now sits at its old index.
     */
    private setContentPreview(hits: GlobalSearchResult[]): void {
        const hadRow = this.contentPreview().length > 0;
        const hasRow = hits.length > 0;
        const selected = this.selectedIndex();
        if (hadRow !== hasRow && selected >= 0) {
            this.selectedIndex.set(hasRow ? selected + 1 : selected - 1);
        }
        this.contentPreview.set(hits);
    }

    /** Switches to the slides-and-videos search for the current query, keeping the course filters. */
    protected showContentHits(): void {
        // A type chip cannot stay next to the slides-and-videos chip, which reads a different index; course chips can.
        const courseTokens = this.tokens().filter((token) => token.facet !== 'type');
        this.applyTokens([...courseTokens, { facet: 'type', value: LECTURE_CONTENT_TYPE }]);
        this.focusInput();
    }

    /** Loads the longer result list for the current search; the next search starts at the default page size again. */
    protected showMoreResults(): void {
        this.isLoading.set(true);
        this.searchSubject.next({ query: this.lastSearchText, limit: EXPANDED_RESULT_LIMIT });
    }

    protected onSearchInput(query: string): void {
        this.searchQuery.set(query);
        this.searchError.set(undefined);

        // Plain typing with the root picker open means the user is searching, not filtering: the picker has
        // three fixed rows and never needed a type-ahead, so it steps aside instead of narrowing.
        if (this.filterPickerOpen() && !this.operator()) {
            this.filterPickerOpen.set(false);
            this.filter.excludeMode.set(false);
        }
        if (!this.operator()) {
            // Typing past a facet operator cancels a chip that was being re-picked.
            this.editingChip.set(-1);
        }
        this.menuActiveIndex.set(0);

        // Only the trailing operator is stripped from the query, so the text in front of it keeps searching
        // behind an open menu. Pushing only on a change stops the value type-ahead firing a request per keystroke.
        const text = this.searchText();
        if (text !== this.lastSearchText) {
            this.lastSearchText = text;
            const hasFilter =
                this.typesParam() !== undefined || this.excludeTypesParam() !== undefined || this.courseIdsParam().length > 0 || this.excludeCourseIdsParam().length > 0;
            if (text.length > 0 || hasFilter) {
                // Show the skeleton immediately while the debounce waits, for a responsive feel.
                this.isLoading.set(true);
            }
            this.searchSubject.next({ query: text });
            this.requestContentPreview(text);
        }
        this.returnHomeIfEmpty();
    }

    /** Backspace with the cursor at the start of the input selects the last chip; a second Backspace removes it. */
    protected onBackspaceAtStart(event: KeyboardEvent) {
        if (this.filter.selectLastChipOnBackspace(event)) {
            this.recordChipKeyOrigin();
            // The chips row is now the active zone, so no result row stays highlighted alongside the chip.
            this.selectedIndex.set(-1);
        }
    }

    /** Removes the chip at the given index (its remove button was clicked). */
    protected onChipRemoved(index: number) {
        this.filter.onChipRemoved(index);
        this.returnHomeIfNothingLeft();
    }

    /** Taps a chip to re-pick its value: reopens that facet's menu; choosing a value replaces the chip. */
    protected onChipSelected(index: number) {
        this.filter.onChipSelected(index);
    }

    /** Applies the chosen value-menu / picker option (delegates to the filter store). */
    protected onOptionSelected(index: number) {
        this.filter.onOptionSelected(index);
    }

    protected onOptionHovered(index: number) {
        this.filter.onOptionHovered(index);
    }

    /** Follows the menu's back control: one level up inside the picker, or out to the results behind it. */
    protected onFilterBack() {
        if (this.backDestination() === 'results') {
            this.filter.leaveFilterMenu();
            this.focusInput();
            return;
        }
        this.filter.back();
    }

    /** Shows the guided filter picker (facet chooser). */
    protected openFilterPicker() {
        this.filter.openFilterPicker();
    }

    /**
     * Switches between the filter menu and the results (Cmd/Ctrl+F and the Filter button). Both are real
     * destinations now that the search text survives filter composition. When nothing sits behind the menu it
     * is the home screen, so the shortcut stays on it rather than exposing the pane the picker replaced.
     */
    protected toggleFilterMenu() {
        if (this.filterMenuOpen() && this.hasPaneBehindFilterMenu()) {
            this.filter.leaveFilterMenu();
            this.focusInput();
            return;
        }
        this.openFilterPicker();
    }

    /**
     * Leaves the filter surface after Escape at its root level. The guided picker is the modal's home screen, so
     * it is only dismissed onto a pane that actually exists; with nothing behind it there is nowhere to go and the
     * overlay closes, which is what the footer's "ESC to close" hint promises.
     */
    private exitFilterMenu() {
        if (this.hasPaneBehindFilterMenu()) {
            this.focusInput();
            return;
        }
        this.overlay.close();
    }

    /**
     * Returns to the home screen once the search box is empty and no filter is left, so an emptied search lands on
     * the guided picker rather than on an empty results pane. Called from the individual gestures rather than
     * from applyTokens, which also runs for the context filters applied on open.
     */
    private returnHomeIfEmpty() {
        if (this.tokens().length === 0 && !this.searchQuery().trim()) {
            this.filter.openFilterPicker();
        }
    }

    /**
     * The same, for the gestures that remove a filter rather than edit the text.
     * <p>
     * Removal asks the question of the search text rather than of the raw input, because a trailing `facet:` means
     * opposite things in the two gestures. While the user is typing it is an operator they are composing and its
     * value menu has to stay open, so the raw input counts as content. Left behind after the last chip is removed it
     * is an unfinished operator in front of nothing, and reading it as content is what pinned the palette on an empty
     * value menu with the results pane unreachable behind it and no way out but closing the overlay.
     */
    private returnHomeIfNothingLeft() {
        if (this.tokens().length === 0 && !this.searchText()) {
            this.filter.openFilterPicker();
        }
    }

    /**
     * Runs Iris content search for the slides and videos filter. It never falls back to the metadata search, which
     * covers a different collection and would answer with entities instead of slides or videos. A failure is
     * reported instead: a 403 means Iris is switched off for every course in scope, anything else means content
     * search is unavailable.
     *
     * @param query         the trimmed search term
     * @param hasValidQuery whether the term is long enough to search; without one the view prompts for it
     * @return the mapped content hits, or an empty list alongside a prompt or an error message
     */
    private searchLectureContent(query: string, hasValidQuery: boolean): Observable<GlobalSearchResult[]> {
        this.searchError.set(undefined);
        if (!hasValidQuery) {
            this.isLoading.set(false);
            return of([]);
        }
        if (!this.availability.contentSearchAvailable()) {
            this.isLoading.set(false);
            this.searchError.set('global.search.contentSearchUnavailable');
            return of([]);
        }
        const courseIds = this.courseIdsParam();
        const excludeCourseIds = this.excludeCourseIdsParam();
        this.isLoading.set(true);
        return timer(SEARCH_DEBOUNCE_MS).pipe(
            switchMap(() =>
                this.lectureSearchService
                    .search(query, CONTENT_SEARCH_LIMIT, courseIds.length ? courseIds : undefined, excludeCourseIds.length ? excludeCourseIds : undefined)
                    .pipe(
                        timeout(CONTENT_SEARCH_TIMEOUT_MS),
                        map((results) => results.map(mapLectureContentResult)),
                        catchError(() => {
                            this.isLoading.set(false);
                            this.searchError.set('global.search.contentSearchUnavailable');
                            return of([]);
                        }),
                    ),
            ),
        );
    }

    /** Stable cache key for placeholder (empty-query) results, keyed by the active filter set. */
    private filterCacheKey(types: string | undefined, excludeTypes: string | undefined, courseIds: number[], excludeCourseIds: number[], limit = DEFAULT_RESULT_LIMIT): string {
        const ids = [...courseIds].sort((a, b) => a - b).join('.');
        const excludeIds = [...excludeCourseIds].sort((a, b) => a - b).join('.');
        return `${types ?? ''}_e${excludeTypes ?? ''}_${ids}_x${excludeIds}_l${limit}`;
    }

    /**
     * Commits a new token set: updates the signal, optimistically shows the skeleton unless the
     * placeholder result is cached, and re-triggers the search.
     */
    private applyTokens(tokens: FilterToken[]) {
        this.tokens.set(tokens);
        const query = this.searchText();
        const cacheKey = this.filterCacheKey(this.typesParam(), this.excludeTypesParam(), this.courseIdsParam(), this.excludeCourseIdsParam());
        const hasCached = !query && this.placeholderCache.has(cacheKey);
        if (!hasCached) {
            this.isLoading.set(true);
        }
        this.lastSearchText = query;
        this.searchSubject.next({ query });
        this.requestContentPreview(query);
    }

    private resetSearch() {
        // Tab availability can change while the palette is closed (an exam becomes visible), so each opening reads it again.
        this.scopedCourseTabs.set(undefined);
        this.lastSearchText = '';
        this.chipKeyOrigin = undefined;
        this.searchSubject.next(null);
        this.contentPreviewSubject.next(null);
        this.filter.reset();
        this.results.set([]);
        this.selectedIndex.set(-1);
        this.hasSearched.set(false);
        this.isLoading.set(false);
        this.searchError.set(undefined);
        this.placeholderCache.clear();
    }

    protected focusInput() {
        // setTimeout(0) defers focus until after PrimeNG's dialog focus trap has run
        setTimeout(() => {
            this.searchInputComponent()?.focusInput();
        }, 0);
    }

    /** A click anywhere ends the keyboard highlight of a chip; clicking a chip itself still re-picks or removes it. */
    @HostListener('document:pointerdown')
    onPointerDown() {
        if (this.selectedChip() >= 0) {
            this.selectedChip.set(-1);
        }
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (this.isToggleShortcut(event)) {
            event.preventDefault();
            this.overlay.toggle();
            return;
        }
        if (!this.overlay.isOpen()) return;

        if (this.isTypedBeforeInputFocus(event)) {
            // The dialog focuses the input only after its open animation, so the first letters of a fast typist arrive
            // while the page still has focus. They go into the search box instead of getting lost.
            event.preventDefault();
            this.onSearchInput(this.searchQuery() + event.key);
            this.focusInput();
            return;
        }

        // Cmd/Ctrl+F goes to the guided filter picker (OS-appropriate modifier, consistent with Cmd/Ctrl+K).
        // Always preventDefault, including where the shortcut is inert, so the browser find bar can never open
        // on top of the modal; acting only on the initial press keeps auto-repeat quiet.
        if (event.key.toLowerCase() === 'f' && this.osDetector.isActionKey(event)) {
            event.preventDefault();
            if (!event.repeat) {
                this.toggleFilterMenu();
            }
            return;
        }

        // Menu mode: while a value menu or the filter picker is open, arrows/enter/tab/escape drive it.
        if (this.filterMenuOpen()) {
            this.filter.handleMenuKey(event);
            return;
        }

        if (this.selectedChip() >= 0 && CHIP_KEYS.has(event.key) && (!this.isFromChipKeyOrigin(event) || this.isTextNavigation(event))) {
            // Clicking into the text, moving the cursor, selecting text or focusing another element leaves the chip
            // highlighted, but the key is meant for what the user moved to: the chip lets go and the key acts natively.
            // The same goes for a modified arrow (word or line jumps, selecting), which only ever means the text.
            this.selectedChip.set(-1);
        }

        switch (event.key) {
            case 'Escape':
                event.preventDefault();
                if (this.selectedChip() >= 0) {
                    this.exitChips();
                } else {
                    this.overlay.close();
                }
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.moveDown();
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.moveUp();
                break;
            case 'ArrowLeft':
                // Only meaningful once inside the chips row.
                if (this.selectedChip() >= 0) {
                    event.preventDefault();
                    this.selectedChip.update((i) => Math.max(0, i - 1));
                }
                break;
            case 'ArrowRight':
                if (this.selectedChip() >= 0) {
                    event.preventDefault();
                    this.selectedChip.update((i) => Math.min(this.tokens().length - 1, i + 1));
                }
                break;
            case 'Enter':
                // Enter on a keyboard-selected chip re-picks it, the same as clicking it. Once focus moved on, for example
                // with Tab to the Filter button, Enter belongs to the focused control and the chip only stays highlighted.
                // A modified Enter (Cmd/Ctrl+Enter) continues the Iris answer in the chat instead.
                if (
                    event.metaKey ||
                    event.ctrlKey ||
                    event.defaultPrevented ||
                    !this.isFocusUnmovedSinceHighlight() ||
                    (event.target instanceof Element && event.target.closest('jhi-global-search-iris-answer'))
                ) {
                    break;
                }
                if (this.selectedChip() >= 0) {
                    event.preventDefault();
                    this.onChipSelected(this.selectedChip());
                }
                break;
            case 'Backspace':
            case 'Delete':
                // A handled event is the keystroke that just selected the chip. A held key keeps removing, one chip per
                // key repeat, as in Gmail.
                if (this.selectedChip() >= 0 && !event.defaultPrevented) {
                    event.preventDefault();
                    this.removeSelectedChip();
                }
                break;
        }
    }

    /** An arrow with a modifier jumps or selects within the text; only a plain arrow moves between chips. */
    private isTextNavigation(event: KeyboardEvent): boolean {
        const arrow = event.key === 'ArrowLeft' || event.key === 'ArrowRight';
        return arrow && (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
    }

    private recordChipKeyOrigin() {
        const target = this.document.activeElement;
        const input = target instanceof HTMLInputElement ? target : undefined;
        this.chipKeyOrigin = { target, selectionStart: input?.selectionStart ?? null, selectionEnd: input?.selectionEnd ?? null };
    }

    /** Whether focus is still on the element that had it when the chip was highlighted. */
    private isFocusUnmovedSinceHighlight(): boolean {
        return this.chipKeyOrigin !== undefined && this.document.activeElement === this.chipKeyOrigin.target;
    }

    /** Whether the key comes from where the chip was highlighted, with the cursor and text selection unchanged. */
    private isFromChipKeyOrigin(event: KeyboardEvent): boolean {
        const origin = this.chipKeyOrigin;
        if (!origin || event.target !== origin.target) {
            return false;
        }
        const input = event.target instanceof HTMLInputElement ? event.target : undefined;
        return (input?.selectionStart ?? null) === origin.selectionStart && (input?.selectionEnd ?? null) === origin.selectionEnd;
    }

    /** Down walks the zones: search input -> results list; a highlighted chip goes to the results too. */
    private moveDown() {
        if (this.selectedChip() >= 0) {
            // Chips -> results.
            this.selectedChip.set(-1);
            this.selectedIndex.set(this.maxIndex() >= 0 ? 0 : -1);
            return;
        }
        if (this.selectedIndex() < 0) {
            // Search input -> results, as in other command palettes. The chips are reached with Backspace or a click, so
            // the course chip a course page adds does not cost an extra key press before the first result.
            this.selectedIndex.set(this.maxIndex() >= 0 ? 0 : -1);
            return;
        }
        // Within results.
        this.selectedIndex.update((i) => Math.min(i + 1, this.maxIndex()));
    }

    /** Up walks the zones: results list -> search input; a highlighted chip goes back to the search input. */
    private moveUp() {
        if (this.selectedChip() >= 0) {
            // Chips -> search input.
            this.exitChips();
            return;
        }
        if (this.selectedIndex() > 0) {
            this.selectedIndex.update((i) => i - 1);
            return;
        }
        if (this.selectedIndex() === 0) {
            // Top of results -> back to the search input.
            this.selectedIndex.set(-1);
            this.focusInput();
        }
    }

    private exitChips() {
        this.selectedChip.set(-1);
        this.focusInput();
    }

    private removeSelectedChip() {
        const index = this.selectedChip();
        this.applyTokens(removeTokenAt(this.tokens(), index));
        const remaining = this.tokens().length;
        if (remaining === 0) {
            this.exitChips();
            this.returnHomeIfNothingLeft();
        } else {
            this.selectedChip.set(Math.min(index, remaining - 1));
        }
    }

    /**
     * Runs when the dialog has finished closing and released its focus trap. A palette closed without opening anything
     * (Escape, the shortcut, a click outside) gives the focus back to where the user opened it from; after opening a
     * result, the new page owns the focus.
     */
    protected onDialogHide(): void {
        this.overlay.close();
        const origin = this.focusOrigin;
        this.focusOrigin = undefined;
        if (origin && origin.url === this.router.url && origin.element.isConnected && origin.element !== this.document.body) {
            origin.element.focus();
        }
    }

    /** A printable key typed while the focus is still outside the open palette. */
    private isTypedBeforeInputFocus(event: KeyboardEvent): boolean {
        if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey || this.operator()) {
            return false;
        }
        const active = this.document.activeElement;
        return !active || !active.closest('.p-dialog');
    }

    private isToggleShortcut(event: KeyboardEvent): boolean {
        return event.key.toLowerCase() === 'k' && this.osDetector.isActionKey(event) && this.accountService.isAuthenticated() && !event.repeat;
    }
}
